import { useState, useEffect, useRef, useMemo } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import { Loader2, CheckCircle, AlertCircle, Layers, Users, Zap, MessageSquare, RefreshCw } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'

const STAGE_NAMES = [
  'initial_cleanup',
  'numerical_detection',
  'header_classification',
  'prediction_rewrite',
  'demographic_consolidation',
  'hf_consolidation',
  'pii_redaction_scan',
  'embedding_index',
  'bootstrap_config_space',
  'research_question_suggestion',
  'first_pre_read',
  'smart_boost_readiness',
  'twin_panel_builder',
  'holdout_backtest',
  'twin_accuracy_aggregator',
]

const TWIN_STAGE_DISPLAY: Record<string, string> = {
  twin_panel_builder: 'Building 1:1 twins from your respondents',
  holdout_backtest: 'Backtesting twin accuracy against held-out human responses',
  twin_accuracy_aggregator: 'Computing per-twin accuracy and confidence',
}

const STAGE_DETAIL: Record<string, string> = {
  initial_cleanup: 'Removing empty columns and normalising response text',
  numerical_detection: 'Identifying numeric scale columns',
  header_classification: 'Classifying columns as demographic, prediction, or open-ended',
  prediction_rewrite: 'Rewriting prediction column headers for clarity',
  demographic_consolidation: 'Grouping demographic segments across your respondents',
  hf_consolidation: 'Consolidating high-frequency open-ended responses',
  pii_redaction_scan: 'Scanning for personally identifiable information',
  embedding_index: 'Creating semantic embeddings to power search and similarity',
  bootstrap_config_space: 'Setting up prediction configurations',
  research_question_suggestion: 'Generating tailored research questions from your data',
  first_pre_read: 'Creating your first pre-read report',
  smart_boost_readiness: 'Checking data quality for smart prediction boost',
  twin_panel_builder: 'Building a digital twin for each respondent — this is the longest step',
  holdout_backtest: 'Running accuracy tests using held-out responses',
  twin_accuracy_aggregator: 'Computing final accuracy and confidence scores',
  csv_derived_templates: 'Generating question templates from your survey columns',
}

const LONG_WAIT_MESSAGES = [
  'Still going — building accurate twins takes a few minutes.',
  'Each twin is tested against held-out responses to verify accuracy.',
  'For a 1,000-respondent dataset this usually takes 4–8 minutes.',
  'The wait is worth it — you\'ll see exactly where the panel is right and where it misses.',
  'Almost there — finalising accuracy scores and templates.',
]

function toDisplayName(name: string): string {
  return TWIN_STAGE_DISPLAY[name] ?? name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

type StageStatus = 'success' | 'running' | 'failed' | 'skipped' | 'pending'

interface StageEntry {
  name: string
  status: StageStatus
  duration_ms?: number
  counts?: Record<string, number>
  error?: { message: string }
}

type RunStatus = 'pending' | 'running' | 'success' | 'partial' | 'failed'

interface PipelineRun {
  status: RunStatus
  stages?: StageEntry[]
  total_cost_usd?: number
  started_at?: string
  total_errors?: number
}

function firstCountValue(counts: Record<string, number> | undefined): string | null {
  if (!counts) return null
  const entries = Object.values(counts)
  if (entries.length === 0) return null
  return String(entries[0])
}

interface RecognitionData {
  recognized: boolean
  filename?: string
  row_count?: number
  column_count?: number | null
  dedup_ratio?: number | null
  segments?: Array<{ name: string; source_column?: string; respondent_count: number }>
}

export function PipelineProcessingPage() {
  const [searchParams] = useSearchParams()
  const pipelineRunId = searchParams.get('pipeline_run_id')
  const projectId = searchParams.get('project_id')
  const navigate = useNavigate()

  const [run, setRun] = useState<PipelineRun | null>(null)
  const [elapsedMs, setElapsedMs] = useState(0)
  const pollingStartRef = useRef<number>(Date.now())
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [recognition, setRecognition] = useState<RecognitionData | null>(null)
  const recognitionIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const funnelFiredRef = useRef(false)
  const [firstTemplateId, setFirstTemplateId] = useState<string | null>(null)
  const uploadConfirmedFiredRef = useRef(false)

  const [isRetrying, setIsRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  const [longWaitIdx, setLongWaitIdx] = useState(0)

  const isTerminal = (status: RunStatus) =>
    status === 'success' || status === 'partial' || status === 'failed'

  const isComplete = run?.status === 'success' || run?.status === 'partial'

  const handleRetry = async () => {
    if (!pipelineRunId) return
    setIsRetrying(true)
    setRetryError(null)
    try {
      const sessionToken = localStorage.getItem('echo_session')
      const hdrs: Record<string, string> = { 'Content-Type': 'application/json' }
      if (sessionToken) hdrs['Authorization'] = `Bearer ${sessionToken}`
      const res = await fetch(`/api/v1/pipeline-runs/${pipelineRunId}/retry`, { method: 'POST', headers: hdrs })
      if (!res.ok) throw new Error(`${res.status}`)
      const data = await res.json() as { pipeline_run_id: string }
      navigate(`/app/pipeline?pipeline_run_id=${data.pipeline_run_id}&project_id=${projectId ?? ''}`)
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : 'Retry failed')
      setIsRetrying(false)
    }
  }

  // Long-wait ticker — cycles engagement message every 90s after 2min
  useEffect(() => {
    if (isComplete || run?.status === 'failed') return
    const tick = setInterval(() => {
      const elapsed = Date.now() - pollingStartRef.current
      if (elapsed > 120_000) {
        setLongWaitIdx(i => (i + 1) % LONG_WAIT_MESSAGES.length)
      }
    }, 90_000)
    return () => clearInterval(tick)
  }, [isComplete, run?.status])

  // Polling
  useEffect(() => {
    if (!pipelineRunId) return
    pollingStartRef.current = Date.now()

    const poll = async () => {
      try {
        const sessionToken = localStorage.getItem('echo_session')
        const headers: Record<string, string> = {}
        if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`

        const res = await fetch(`/api/v1/pipeline-runs/${pipelineRunId}`, { headers })
        if (!res.ok) return

        const data = await res.json() as PipelineRun
        setRun(data)

        if (isTerminal(data.status) && intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
          if (recognitionIntervalRef.current) {
            clearInterval(recognitionIntervalRef.current)
            recognitionIntervalRef.current = null
          }
        }
      } catch {
        // network error — keep polling
      }
    }

    poll()
    intervalRef.current = setInterval(poll, 2000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [pipelineRunId])

  // Stage-5 recognition polling
  useEffect(() => {
    if (!projectId) return

    const pollRecognition = async () => {
      try {
        const sessionToken = localStorage.getItem('echo_session')
        const headers: Record<string, string> = {}
        if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`

        const res = await fetch(`/api/v1/projects/${projectId}/recognition`, { headers })
        if (!res.ok) return

        const data = await res.json() as RecognitionData
        if (data.recognized) {
          setRecognition(data)
          if (recognitionIntervalRef.current) {
            clearInterval(recognitionIntervalRef.current)
            recognitionIntervalRef.current = null
          }
        }
      } catch {
        // network error — keep polling
      }
    }

    pollRecognition()
    recognitionIntervalRef.current = setInterval(pollRecognition, 3000)

    return () => {
      if (recognitionIntervalRef.current) clearInterval(recognitionIntervalRef.current)
    }
  }, [projectId])

  // Fire recognition_card_shown funnel event once when card first renders
  useEffect(() => {
    if (!recognition?.recognized || !projectId || funnelFiredRef.current) return
    funnelFiredRef.current = true
    const sessionToken = localStorage.getItem('echo_session')
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`
    fetch(`/api/v1/projects/${projectId}/funnel`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ event: 'recognition_card_shown' }),
    }).catch(() => {})
  }, [recognition?.recognized, projectId])

  // Fire upload_confirmed funnel event on mount (AHA-03, AHA-04 TTI start timestamp)
  useEffect(() => {
    if (!projectId || uploadConfirmedFiredRef.current) return
    uploadConfirmedFiredRef.current = true
    const sessionToken = localStorage.getItem('echo_session')
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`
    fetch(`/api/v1/projects/${projectId}/funnel`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ event: 'upload_confirmed' }),
    }).catch(() => {})
  }, [])

  // Auto-redirect on pipeline completion to Project Home with first template pre-selected
  useEffect(() => {
    if (!isComplete || !projectId) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const sessionToken = localStorage.getItem('echo_session')
    const headers: Record<string, string> = {}
    if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`
    fetch(`/api/v1/projects/${projectId}/derived-templates`, { headers })
      .then(r => r.ok ? r.json() as Promise<{ templates: Array<{ template_id: string }> }> : { templates: [] })
      .then(data => {
        const first = data.templates?.[0]?.template_id ?? null
        setFirstTemplateId(first)
        const target = first
          ? `/app/projects/${projectId}?template=${first}`
          : `/app/projects/${projectId}`
        timer = setTimeout(() => navigate(target), 1500)
      })
      .catch(() => {
        // fetch failed — still redirect after 1500ms without template param
        timer = setTimeout(() => navigate(`/app/projects/${projectId}`), 1500)
      })
    return () => { if (timer) clearTimeout(timer) }
  }, [isComplete, projectId, navigate])

  // Elapsed timer — updates every second
  useEffect(() => {
    const tick = setInterval(() => {
      setElapsedMs(Date.now() - pollingStartRef.current)
    }, 1000)
    return () => clearInterval(tick)
  }, [])

  const stages: StageEntry[] = run?.stages ?? []

  // Narrative beats — derived from completed stage counts
  const narrativeBeats = useMemo(() => {
    const beats: { icon: typeof Layers; label: string; detail: string; key: string }[] = []

    const headerStage = stages.find(s => s.name === 'header_classification' && s.status === 'success')
    if (headerStage?.counts) {
      const c = headerStage.counts
      const parts = [
        c.demographic != null ? `${c.demographic} demographic` : null,
        c.prediction != null ? `${c.prediction} prediction` : null,
        c.open_ended != null ? `${c.open_ended} open-ended` : null,
      ].filter(Boolean)
      const total = Object.values(c).reduce((a, b) => a + b, 0)
      beats.push({
        key: 'header_classification',
        icon: Layers,
        label: 'Columns recognised',
        detail: `${total} columns: ${parts.join(', ')}`,
      })
    }

    const demoStage = stages.find(s => s.name === 'demographic_consolidation' && s.status === 'success')
    if (demoStage?.counts) {
      const segCount = demoStage.counts.segments ?? demoStage.counts.groups ?? Object.values(demoStage.counts)[0]
      if (segCount != null) {
        beats.push({
          key: 'demographic_consolidation',
          icon: Users,
          label: 'Segments identified',
          detail: `${segCount} demographic segment${segCount !== 1 ? 's' : ''} found across your respondents`,
        })
      }
    }

    const embedStage = stages.find(s => s.name === 'embedding_index' && s.status === 'success')
    if (embedStage?.counts) {
      const raw = embedStage.counts.raw_rows ?? embedStage.counts.rows
      const unique = embedStage.counts.unique_patterns ?? embedStage.counts.indexed
      if (raw != null && unique != null && raw > 0) {
        const compression = (raw / Math.max(unique, 1)).toFixed(1)
        beats.push({
          key: 'embedding_index',
          icon: Zap,
          label: 'Patterns compressed',
          detail: `${raw.toLocaleString()} rows → ${unique.toLocaleString()} unique patterns (${compression}× compression)`,
        })
      }
    }

    const rqStage = stages.find(s => s.name === 'research_question_suggestion' && s.status === 'success')
    if (rqStage?.counts) {
      const n = rqStage.counts.questions ?? rqStage.counts.count ?? Object.values(rqStage.counts)[0]
      if (n != null) {
        beats.push({
          key: 'research_question_suggestion',
          icon: MessageSquare,
          label: 'Research questions ready',
          detail: `${n} tailored questions generated from your survey columns`,
        })
      }
    }

    return beats
  }, [stages])

  // Narrative page title
  const narrativeTitle = useMemo(() => {
    const doneCnt = stages.filter(s => s.status === 'success').length
    if (isComplete) return 'Your panel is ready'
    if (doneCnt >= 12) return 'Final touches…'
    if (doneCnt >= 9) return 'Building your twin panel…'
    if (doneCnt >= 6) return 'Indexing your respondents…'
    if (doneCnt >= 3) return 'Classifying columns…'
    return 'Starting pipeline…'
  }, [stages, isComplete])

  // Determine current stage index from stages array
  const currentStageIndex = (() => {
    // Find the last running stage, or last completed stage + 1
    let runningIdx = -1
    let lastDone = -1
    stages.forEach((s, i) => {
      if (s.status === 'running') runningIdx = i
      if (s.status === 'success' || s.status === 'failed' || s.status === 'skipped') lastDone = i
    })
    if (runningIdx !== -1) return runningIdx
    return lastDone + 1
  })()

  const recentStages = stages.slice(-5)
  const elapsedSec = (elapsedMs / 1000).toFixed(0)
  const totalCost = run?.total_cost_usd ?? 0
  const errorCount = run?.total_errors ?? 0
  const completionTarget = projectId
    ? firstTemplateId
      ? `/app/projects/${projectId}?template=${firstTemplateId}`
      : `/app/projects/${projectId}`
    : '/app/demo'

  return (
    <div className="min-h-screen bg-paper flex flex-col items-center px-4 py-16">
      <div className="w-full max-w-2xl space-y-10">

        {/* ── Narrative title ── */}
        <div>
          <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>{narrativeTitle}</h1>
          {!isComplete && <p className="text-sm text-slate-500 mt-1">Processing your data — this usually takes 2–4 minutes.</p>}
        </div>

        {/* ── Progress bar: 15 segments ── */}
        <div className="space-y-2" data-testid="pipeline-status" data-status={isComplete ? 'complete' : 'running'}>
          <p className="text-sm font-medium text-deep-ink">Pipeline progress</p>
          <div className="flex gap-1.5 flex-wrap">
            {STAGE_NAMES.map((name, idx) => {
              const stageEntry = stages.find(s => s.name === name)
              const isDone = stageEntry?.status === 'success' || stageEntry?.status === 'failed' || stageEntry?.status === 'skipped'
              const isCurrent = idx === currentStageIndex && !isComplete

              return (
                <div
                  key={name}
                  title={toDisplayName(name)}
                  className={`h-2 w-8 rounded-full transition-colors ${
                    isDone
                      ? 'bg-echo-indigo'
                      : isCurrent
                      ? 'bg-indigo-400 animate-pulse'
                      : 'bg-gray-200'
                  }`}
                />
              )
            })}
          </div>
        </div>

        {/* ── Narrative insight beats ── */}
        {narrativeBeats.length > 0 && (
          <div className="space-y-2">
            {narrativeBeats.map(beat => (
              <div key={beat.key} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-echo-indigo/5 border border-echo-indigo/15">
                <beat.icon className="w-4 h-4 text-echo-indigo shrink-0" strokeWidth={1.75} />
                <div>
                  <span className="text-sm font-medium text-deep-ink">{beat.label}</span>
                  <span className="text-sm text-slate-500 ml-1.5">{beat.detail}</span>
                </div>
                <CheckCircle className="w-4 h-4 text-validation-green shrink-0 ml-auto" strokeWidth={1.75} />
              </div>
            ))}
          </div>
        )}

        {/* ── Recognition mini-card (inline, appears when Stage 5 completes) ── */}
        {recognition?.recognized && (
          <div data-testid="recognition-card-inline">
            <Card
              className="bg-echo-indigo/5 border-echo-indigo/20 opacity-100 transition-opacity duration-300"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2">
                  <p className="text-xs font-medium text-echo-indigo uppercase tracking-wide">Your data, recognized</p>
                  <p className="text-sm font-medium text-deep-ink">{recognition.filename}</p>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                    <span>{recognition.row_count?.toLocaleString()} respondents</span>
                    {recognition.column_count != null && <span>{recognition.column_count} columns</span>}
                    {recognition.dedup_ratio != null && <span>{Math.round(recognition.dedup_ratio * 100)}% unique</span>}
                  </div>
                  {recognition.segments && recognition.segments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {recognition.segments.slice(0, 4).map((s, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full bg-white border border-echo-indigo/20 text-xs text-echo-indigo">
                          {s.name} ({s.respondent_count})
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <CheckCircle className="w-5 h-5 text-validation-green shrink-0 mt-0.5" strokeWidth={1.75} />
              </div>
            </Card>
          </div>
        )}

        {/* ── Recent stages list ── */}
        {recentStages.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-deep-ink">Recent stages</p>
            <div className="rounded-xl border border-border-gray bg-white divide-y divide-border-gray">
              {recentStages.map(stage => (
                <div
                  key={stage.name}
                  data-testid={`pipeline-stage-${stage.name}`}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  {stage.status === 'success' && (
                    <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                  )}
                  {stage.status === 'running' && (
                    <Loader2 className="w-4 h-4 text-echo-mark animate-spin shrink-0" />
                  )}
                  {stage.status === 'failed' && (
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                  )}
                  {stage.status === 'pending' && (
                    <div className="w-4 h-4 rounded-full border border-gray-300 shrink-0" />
                  )}

                  <span className="text-sm text-deep-ink flex-1">
                    {toDisplayName(stage.name)}
                  </span>

                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    {stage.status === 'success' && stage.duration_ms != null && (
                      <>
                        {(stage.duration_ms / 1000).toFixed(1)}s
                        {firstCountValue(stage.counts) != null && (
                          <> &middot; {firstCountValue(stage.counts)}</>
                        )}
                      </>
                    )}
                    {stage.status === 'running' && (STAGE_DETAIL[stage.name] ?? 'Working…')}
                    {stage.status === 'failed' && (
                      <span className="text-red-500">{stage.error?.message ?? 'Failed'}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Loading placeholder when no stages yet */}
        {recentStages.length === 0 && !isComplete && (
          <div className="flex items-center gap-3 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Initialising pipeline…</span>
          </div>
        )}

        {/* ── Stats footer ── */}
        <div className="flex items-center gap-6 text-sm text-slate-500">
          <span>Elapsed: {elapsedSec}s</span>
          <span>Cost: ${totalCost.toFixed(4)}</span>
          {errorCount > 0 && (
            <span className="text-red-500">{errorCount} error{errorCount !== 1 ? 's' : ''}</span>
          )}
        </div>

        {/* Long-wait engagement message (appears after 2 min) */}
        {!isComplete && run?.status !== 'failed' && elapsedMs > 120_000 && (
          <p className="text-sm text-slate-500 italic">{LONG_WAIT_MESSAGES[longWaitIdx]}</p>
        )}

        {/* ── Completion banner ── */}
        {isComplete && (
          <div
            data-testid="completion-banner"
            className="rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-5 space-y-3"
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" strokeWidth={1.75} />
              <p className="text-base font-medium text-deep-ink">Your panel is ready!</p>
            </div>
            <p className="text-sm text-slate-600">Taking you to your project home…</p>
            <Link
              to={completionTarget}
              className="text-sm text-echo-indigo hover:underline"
            >
              Go now →
            </Link>
          </div>
        )}

        {/* Failed state */}
        {run?.status === 'failed' && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-5 space-y-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-700">Pipeline failed</p>
                <p className="text-xs text-red-600 mt-0.5">
                  Check the stage errors above. Retrying will resume from the first incomplete stage.
                </p>
              </div>
            </div>
            {retryError && (
              <p className="text-xs text-red-600 pl-8">Retry error: {retryError}</p>
            )}
            <div className="pl-8">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRetry}
                disabled={isRetrying}
              >
                {isRetrying
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Retrying…</>
                  : <><RefreshCw className="w-3.5 h-3.5" /> Retry pipeline</>
                }
              </Button>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
